import { jatCharLabel } from "@/lib/jateamento";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { waLink } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, FileDown, FileText, Info, Factory, Paperclip, Send, Upload } from "lucide-react";
import QuoteItems from "@/components/QuoteItems";
import { StatusBadge, brl, fdate } from "@/pages/portal/Dashboard";

const REQ_STATUS = ["Rascunho", "Aguardando conferência", "Em análise", "Em orçamento", "Orçamento conferido",
                    "Orçamento enviado", "Aguardando aprovação", "Aprovado", "Em produção", "Concluído",
                    "Entregue", "Reprovado", "Cancelado"];

const JAT_DIFF_LABELS = { baixa: "Baixa", media: "Média", alta: "Alta", muito_alta: "Muito alta" };

const inputCls =
  "mt-1.5 w-full bg-slate-900/70 border border-slate-700 rounded-md px-3 py-2.5 text-sm outline-none focus:border-orange-500 transition-colors placeholder:text-slate-600";
const labelCls = "text-xs uppercase tracking-wider text-slate-400";

const fdt = (iso) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—";

function Field({ label, value, wa }) {
  const link = wa ? waLink(value) : "";
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      {link ? (
        <a href={link} target="_blank" rel="noreferrer" data-testid={`wa-link-${label.toLowerCase().replace(/[^a-z]/g, "")}`}
          className="mt-0.5 inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300 transition-colors">
          {value} ↗
        </a>
      ) : (
        <div className="mt-0.5 text-sm text-slate-200">{value || "—"}</div>
      )}
    </div>
  );
}

export default function SolicitacaoDetalhe() {
  const { id } = useParams();
  const [q, setQ] = useState(null);
  const [status, setStatus] = useState("Em análise");
  const [prodDate, setProdDate] = useState("");
  const [valDays, setValDays] = useState(3);
  const [payKey, setPayKey] = useState("");
  const [payOptions, setPayOptions] = useState([]);
  const [internalNotes, setInternalNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [opLoading, setOpLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachItem, setAttachItem] = useState("");
  const attachRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/quote-requests/${id}`);
        setQ(data);
        setStatus(data.status || "Em análise");
        setProdDate(data.pricing?.production_date || "");
        setValDays(parseInt(data.pricing?.validity) || 3);
        setPayKey(data.payment_method || "");
        setInternalNotes(data.pricing?.internal_notes || "");
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
    api.get(`/quote-requests/${id}/payment-options`).then(({ data }) => setPayOptions(data.options || [])).catch(() => {});
  }, [id]);

  const generateOp = async () => {
    setOpLoading(true);
    try {
      const { data } = await api.post(`/quote-requests/${id}/production-order`);
      setQ((prev) => ({ ...prev, production_order: { id: data.id, number: data.number } }));
      if (data.existing) {
        toast.info(`Ordem de Produção ${data.number} já existia — reutilizada, sem duplicar.`);
      } else {
        toast.success(`Ordem de Produção ${data.number} gerada!`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setOpLoading(false);
    }
  };

  const reload = async () => {
    try {
      const { data } = await api.get(`/quote-requests/${id}`);
      setQ(data);
      setStatus(data.status);
      setProdDate(data.pricing?.production_date || "");
      setValDays(parseInt(data.pricing?.validity) || 3);
      setPayKey(data.payment_method || "");
      setInternalNotes(data.pricing?.internal_notes || "");
    } catch {}
  };

  const buildPayload = (statusOverride) => ({
    status: statusOverride || status,
    payment_method: payKey,
    pricing: {
      ...(q.pricing || {}),
      production_date: prodDate || null,
      validity: `${Number(valDays) || 3} dias`,
      payment_terms: payKey
        ? (payOptions.find((o) => o.key === payKey)?.label || q.pricing?.payment_terms || null)
        : null,
      internal_notes: internalNotes,
    },
  });

  const sendQuote = async () => {
    if (!(Number(q?.value) > 0)) {
      toast.error("O valor total vem da soma dos itens — calcule ao menos um item antes de enviar.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/quote-requests/${id}`, buildPayload("Orçamento enviado"));
      setQ(data);
      toast.success("Orçamento enviado ao cliente — responsável pela liberação registrado.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const download = async (idx, name) => {
    try {
      const { data } = await api.get(`/quote-requests/${id}/files/${idx}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadPdf = async () => {
    try {
      const { data } = await api.get(`/quote-requests/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${q.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadAttachment = async (file) => {
    setUploading(true);
    try {
      const params = new URLSearchParams();
      if (attachItem) params.set("item", attachItem);
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/quote-requests/${id}/files?${params.toString()}`, fd);
      toast.success(`Arquivo "${file.name}" anexado ao pedido.`);
      await reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async (statusOverride) => {
    setSaving(true);
    const hadOp = !!q?.production_order;
    try {
      const { data } = await api.put(`/quote-requests/${id}`, buildPayload(statusOverride));
      setQ(data);
      setStatus(data.status || status);
      if (data.op_error) {
        toast.error(data.op_error);
      } else if (data.production_order && !hadOp) {
        toast.success(`Ordem de Produção ${data.production_order.number} criada e já disponível em Produção.`);
      } else {
        toast.success("Orçamento salvo! Cliente será notificado se o status mudou.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!q) {
    return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>;
  }

  const validUntil = (() => {
    try {
      const d = new Date(q.created_at);
      d.setDate(d.getDate() + (Number(valDays) || 0));
      return d.toLocaleDateString("pt-BR");
    } catch {
      return "—";
    }
  })();

  return (
    <div data-testid="admin-request-detail-page">
      <Link to="/solicitacoes" data-testid="admin-request-back-link"
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500 hover:text-orange-400 transition-colors">
        <ArrowLeft className="w-3.5 h-3.5" /> Orçamentos
      </Link>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="display text-3xl font-extrabold uppercase mono" data-testid="admin-request-number">{q.number}</h1>
          <p className="mt-1 text-sm text-slate-500">{fdt(q.created_at)} · {q.user_email}</p>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="admin-request-pdf-button" onClick={downloadPdf}
            className="inline-flex items-center gap-1.5 border border-slate-700 hover:border-orange-500 text-slate-300 hover:text-orange-400 rounded-md px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors">
            <FileDown className="w-4 h-4" /> PDF
          </button>
          {["Aprovado", "Em produção", "Concluído", "Entregue"].includes(q.status) && !q.production_order && (
            <button data-testid="generate-op-button" onClick={generateOp} disabled={opLoading}
              className="inline-flex items-center gap-1.5 pill-accent rounded-md px-4 py-2 text-xs font-semibold uppercase tracking-wider disabled:opacity-60 transition">
              {opLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Factory className="w-4 h-4" />} Gerar OP
            </button>
          )}
          {q.production_order && (
            <Link to="/producao" data-testid="op-link"
              className="inline-flex items-center gap-1.5 border border-emerald-800 text-emerald-400 hover:bg-emerald-500/10 rounded-md px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors">
              <Factory className="w-4 h-4" /> {q.production_order.number}
            </Link>
          )}
          <StatusBadge status={q.status} />
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-5">
        <section className="card-tactical rounded-md p-6">
          <h2 className="display text-lg font-bold uppercase tracking-wide">Dados do Cliente</h2>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <Field label="Nome / Razão Social" value={q.client_name} />
            <Field label="Nome Fantasia" value={q.trade_name} />
            <Field label="CPF / CNPJ" value={q.client_document} />
            <Field label="E-mail" value={q.client_email} />
            <Field label="Telefone" value={q.client_phone} wa />
            <Field label="WhatsApp" value={q.whatsapp} wa />
            <Field label="Endereço" value={[q.address, q.address_number, q.complement].filter(Boolean).join(", ")} />
            <Field label="Bairro" value={q.neighborhood} />
            <Field label="CEP" value={q.cep} />
            <Field label="Cidade/UF" value={[q.city, q.state].filter(Boolean).join("/")} />
          </div>
        </section>

        <section className="card-tactical rounded-md p-6" data-testid="admin-request-schedule">
          <h2 className="display text-lg font-bold uppercase tracking-wide">Prazo e observações do cliente</h2>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <Field label="Prazo desejado" value={q.deadline} />
            <Field label="Urgência" value={q.urgency} />
          </div>
          {q.general_notes && <div className="mt-4"><Field label="Observações gerais" value={q.general_notes} /></div>}
        </section>

        {q.jateamento_request && (
          <section className="card-tactical rounded-md p-6 lg:col-span-2" data-testid="admin-jateamento-request">
            <h2 className="display text-lg font-bold uppercase tracking-wide">Jateamento — dados informados pelo cliente</h2>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Field label="Comprimento (mm)" value={q.jateamento_request.length_mm} />
              <Field label="Largura (mm)" value={q.jateamento_request.width_mm} />
              <Field label="Altura (mm)" value={q.jateamento_request.height_mm} />
              <Field label="Peso aprox. (kg)" value={q.jateamento_request.weight_kg} />
              <Field label="Tipo de peça" value={q.jateamento_request.piece_type || "—"} />
              <Field label="Material da peça" value={q.jateamento_request.part_material ? `${q.jateamento_request.part_material}${q.jateamento_request.part_grade ? ` · ${q.jateamento_request.part_grade}` : ""}${q.jateamento_request.part_thickness_mm ? ` · ${q.jateamento_request.part_thickness_mm} mm` : ""}` : "—"} />
              <Field label="Quantidade de peças" value={q.jateamento_request.quantity} />
              <Field label="Faces a jatear" value={q.jateamento_request.faces} />
              <Field label="Nível de dificuldade" value={JAT_DIFF_LABELS[q.jateamento_request.difficulty] || q.jateamento_request.difficulty} />
            </div>
            {!!(q.jateamento_request.characteristics || []).length && (
              <div className="mt-4">
                <Field label="O que torna a peça mais difícil" value={`${q.jateamento_request.characteristics.map(jatCharLabel).join(", ")}${q.jateamento_request.characteristics_other ? ` — ${q.jateamento_request.characteristics_other}` : ""}`} />
              </div>
            )}
            {q.jateamento_request.notes && (
              <div className="mt-4"><Field label="Observações do jateamento" value={q.jateamento_request.notes} /></div>
            )}
            <p className="mt-3 text-[11px] text-slate-500">
              Crie o item de Jateamento em "Itens do orçamento" (os dados acima já vêm preenchidos) para gerar o pré-orçamento automático.
            </p>
          </section>
        )}
      </div>

      <section className="mt-5 card-tactical rounded-md p-6" data-testid="order-attachments">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="display text-lg font-bold uppercase tracking-wide flex items-center gap-2">
              <Paperclip className="w-4 h-4 text-orange-400" /> Anexos do pedido
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Todos os arquivos do pedido — baixe aqui para enviar à máquina. Cliente não anexou? Anexe você mesmo.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!!(q.items || []).length && (
              <select data-testid="attachment-item-select" value={attachItem}
                onChange={(e) => setAttachItem(e.target.value)}
                className="bg-slate-900/70 border border-slate-700 rounded-md px-2.5 py-2 text-xs text-slate-300 outline-none focus:border-orange-500 transition-colors">
                <option value="">Sem vínculo a item</option>
                {q.items.map((it) => (
                  <option key={it.seq} value={it.seq}>ITEM {String(it.seq).padStart(3, "0")} — {it.part_name}</option>
                ))}
              </select>
            )}
            <input ref={attachRef} type="file" className="hidden" data-testid="attachment-file-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAttachment(f); e.target.value = ""; }} />
            <button type="button" data-testid="attachment-upload-button" disabled={uploading}
              onClick={() => attachRef.current?.click()}
              className="inline-flex items-center gap-1.5 pill-accent rounded-md px-4 py-2 text-xs font-semibold uppercase tracking-wider disabled:opacity-60 transition">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Anexar arquivo
            </button>
          </div>
        </div>
        {!(q.files || []).length ? (
          <p className="mt-4 text-sm text-slate-500" data-testid="order-attachments-empty">
            Nenhum arquivo anexado ainda — use "Anexar arquivo" para adicionar o desenho enviado pelo cliente.
          </p>
        ) : (
          <ul className="mt-4 space-y-2">
            {q.files.map((f, i) => {
              const name = f.original_filename || `arquivo ${i + 1}`;
              const ext = name.toLowerCase().split(".").pop();
              return (
                <li key={i} data-testid={`order-attachment-${i}`}
                  className="flex items-center gap-3 rounded-md border border-slate-800 bg-slate-950/60 px-3 py-2.5">
                  <div className="w-9 h-9 shrink-0 rounded-sm border border-slate-800 bg-slate-950 grid place-content-center">
                    <FileText className="w-4 h-4 text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-200 truncate">{name}</div>
                    <div className="text-[10px] uppercase mono text-slate-600">
                      {ext}{f.item_seq != null ? ` · Item ${String(f.item_seq).padStart(3, "0")}` : ""}
                    </div>
                  </div>
                  <button type="button" data-testid={`order-attachment-download-${i}`}
                    onClick={() => download(i, name)}
                    className="inline-flex items-center gap-1.5 border border-slate-700 hover:border-orange-500 text-slate-300 hover:text-orange-400 rounded-md px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider shrink-0 transition-colors">
                    <FileDown className="w-3.5 h-3.5" /> Baixar
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <div className="mt-5">
        <section className="card-tactical rounded-md p-6" data-testid="admin-request-timeline">
          <h2 className="display text-lg font-bold uppercase tracking-wide">Linha do tempo</h2>
          {!q.timeline?.length ? (
            <p className="mt-3 text-sm text-slate-500">Sem eventos registrados.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {q.timeline.map((t, i) => (
                <li key={i} className="flex gap-3 text-sm" data-testid={`timeline-event-${i}`}>
                  <span className="mono text-xs text-slate-600 whitespace-nowrap pt-0.5">{fdt(t.at)}</span>
                  <span className="text-slate-300">
                    {t.event}{t.to ? <> — <span className="text-orange-400">{t.to}</span></> : ""}
                    {t.by && t.by !== "site" ? <span className="text-slate-600"> · {t.by}</span> : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <QuoteItems quoteId={id} files={q.files || []} items={q.items || []} onChanged={reload} jateamentoRequest={q.jateamento_request} />

      <section className="mt-5 card-tactical rounded-md p-6" data-testid="admin-pricing-section">
        <h2 className="display text-lg font-bold uppercase tracking-wide">Dados do Orçamento</h2>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>Data prevista de produção/entrega</label>
            <input data-testid="pricing-production-date" type="date" value={prodDate}
              onChange={(e) => setProdDate(e.target.value)} className={inputCls} />
            <p className="mt-1 text-[11px] text-slate-600">Referência oficial do prazo — aparece no PDF.</p>
          </div>
          <div>
            <label className={labelCls}>Validade do orçamento (dias)</label>
            <input data-testid="pricing-validity-days" type="number" min={1} value={valDays}
              onChange={(e) => setValDays(e.target.value)} className={inputCls} />
            <p className="mt-1 text-[11px] text-slate-600">Válido até <span className="mono text-orange-400" data-testid="pricing-validity-until">{validUntil}</span></p>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Condições de pagamento</label>
            <select data-testid="pricing-payment-method" value={payKey}
              onChange={(e) => setPayKey(e.target.value)} className={inputCls}>
              <option value="">— definir na conferência —</option>
              {payOptions.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}{o.total != null ? ` — ${brl(o.total)}` : ""}{o.installments ? ` (${o.installments}x de ${brl(o.total / o.installments)})` : ""}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-slate-600">Somente as formas habilitadas no cadastro deste cliente.</p>
          </div>
          <div className="sm:col-span-2 lg:col-span-4">
            <label className={labelCls}>Observações internas</label>
            <textarea data-testid="pricing-internal-notes" rows={3} value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)} className={`${inputCls} resize-y`}
              placeholder="Uso interno — aparece na Ordem de Produção, nunca no PDF do cliente" />
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-4 items-end">
          <div>
            <label className={labelCls}>Status do orçamento</label>
            <select data-testid="admin-request-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
              {REQ_STATUS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2.5 rounded-md border border-orange-900/40 bg-orange-500/5 p-3.5">
          <Info className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
          <p className="text-xs text-slate-400 leading-relaxed">
            Ao salvar com um novo status, o cliente é notificado automaticamente por e-mail (e WhatsApp, quando configurado).
          </p>
        </div>

        {(q.prepared_by || q.released_by) && (
          <div className="mt-3 text-[11px] text-slate-600 space-y-0.5" data-testid="quote-responsibles">
            {q.prepared_by && <p>Preparado por: <span className="text-slate-400">{q.prepared_by}</span>{q.prepared_at ? ` em ${fdate(q.prepared_at)}` : ""}</p>}
            {q.released_by && <p>Liberado por: <span className="text-slate-400">{q.released_by}</span>{q.released_at ? ` em ${fdate(q.released_at)}` : ""}</p>}
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <button data-testid="admin-request-save-button" onClick={() => save()} disabled={saving}
            className="mt-6 inline-flex items-center gap-2 pill-accent rounded-md px-6 py-2.5 text-sm font-semibold uppercase tracking-wider disabled:opacity-60 transition">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar orçamento
          </button>
          <button data-testid="admin-request-draft-button" onClick={() => save("Rascunho")} disabled={saving}
            className="mt-6 inline-flex items-center gap-2 border border-slate-600 text-slate-300 hover:border-slate-400 rounded-md px-6 py-2.5 text-sm font-semibold uppercase tracking-wider disabled:opacity-60 transition-colors">
            <Save className="w-4 h-4" /> Salvar como rascunho
          </button>
          <button data-testid="admin-request-send-button" onClick={sendQuote} disabled={saving}
            className="mt-6 inline-flex items-center gap-2 border border-emerald-800 text-emerald-400 hover:bg-emerald-500/10 rounded-md px-6 py-2.5 text-sm font-semibold uppercase tracking-wider disabled:opacity-60 transition-colors">
            <Send className="w-4 h-4" /> Enviar orçamento
          </button>
        </div>
      </section>
    </div>
  );
}
